/**
 * AI Recruit - Dashboard Page
 * User-centric dashboard with personalized metrics and filtering
 */

import React, { useState } from 'react'
import {
  FileText,
  Briefcase,
  Target,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  RefreshCw,
  Download,
} from 'lucide-react'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts'
import { useDashboard, type DateRangeOption } from '../../hooks/useDashboard'
import DateRangeFilter from '../../components/dashboard/DateRangeFilter'
import JobSubmissionsTracking from '../../components/dashboard/JobSubmissionsTracking'
import SourceAnalyticsCard from '../../components/dashboard/SourceAnalyticsCard'
import { exportUserDashboard } from '../../utils/exportToExcel'
import { usePermission } from '../../hooks/usePermission'

const DashboardPage: React.FC = () => {
  const { can } = usePermission()
  const canExport = can('dashboard.export')
  const [dateRange, setDateRange] = useState<DateRangeOption>('last_7_days')
  const [customStartDate, setCustomStartDate] = useState<Date | undefined>()
  const [customEndDate, setCustomEndDate] = useState<Date | undefined>()

  const { data, loading, error, refetch } = useDashboard({
    dateRange,
    customStartDate,
    customEndDate
  })

  const handleDateRangeChange = (
    range: DateRangeOption,
    startDate?: Date,
    endDate?: Date
  ) => {
    setDateRange(range)
    setCustomStartDate(startDate)
    setCustomEndDate(endDate)
  }

  if (loading) {
    return (
      <div className="space-y-6 p-4 sm:p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-600 text-sm sm:text-base">Loading your dashboard...</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 gap-4 sm:gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card animate-pulse">
              <div className="card-body">
                <div className="h-24 bg-gray-200 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6 p-4 sm:p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-600 text-sm sm:text-base">Unable to load dashboard data</p>
          </div>
        </div>
        
        <div className="card">
          <div className="card-body text-center py-12">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to Load Dashboard</h3>
            <p className="text-gray-600 mb-4 text-sm sm:text-base">{error}</p>
            <button 
              onClick={refetch}
              className="btn-primary inline-flex items-center"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!data) {
    return null
  }

  // Empty state
  if (data.is_empty) {
    return (
      <div className="space-y-6 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-600 text-sm sm:text-base">Welcome to your personalized dashboard</p>
          </div>
          <div className="flex items-center space-x-3">
            <DateRangeFilter
              selectedRange={dateRange}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
              onChange={handleDateRangeChange}
            />
            <button 
              onClick={refetch}
              className="btn-outline btn-sm inline-flex items-center"
              title="Refresh dashboard data"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="card">
          <div className="card-body text-center py-12">
            <Target className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No activity yet</h3>
            <p className="text-gray-600 text-sm sm:text-base">Start by uploading resumes or creating jobs</p>
          </div>
        </div>
      </div>
    )
  }

  const stats = [
    {
      name: 'Resumes Uploaded',
      key: 'resumes_uploaded',
      value: data.stats.resumes_uploaded?.count ?? 0,
      trend: data.stats.resumes_uploaded?.trend ?? null,
      icon: FileText,
    },
    {
      name: 'Jobs Assigned',
      key: 'jobs_created',
      value: data.stats.jobs_created?.count ?? 0,
      trend: data.stats.jobs_created?.trend ?? null,
      icon: Briefcase,
    },
    {
      name: 'Matches Applied',
      key: 'matches_applied',
      value: data.stats.matches_applied?.count ?? 0,
      trend: data.stats.matches_applied?.trend ?? null,
      icon: Target,
    },
  ]

  return (
    <div className="space-y-6 p-4 sm:p-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 text-sm sm:text-base">
            Track your recruitment activity and performance
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <DateRangeFilter
            selectedRange={dateRange}
            customStartDate={customStartDate}
            customEndDate={customEndDate}
            onChange={handleDateRangeChange}
          />
          {canExport && (
            <button
              onClick={() => exportUserDashboard(data!, dateRange)}
              className="btn-outline btn-sm inline-flex items-center gap-1.5"
              title="Download Excel report"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Export</span>
            </button>
          )}
          <button
            onClick={refetch}
            className="btn-outline btn-sm inline-flex items-center"
            title="Refresh dashboard data"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 gap-4 sm:gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {stats.map((stat) => (
          <div key={stat.key} className="card">
            <div className="card-body">
              <div className="flex flex-col items-center text-center">
                {/* Large number at top */}
                <div className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
                  {stat.value.toLocaleString()}
                </div>
                
                {/* Icon and label */}
                <div className="flex items-center space-x-2 mb-3">
                  <stat.icon className="w-5 h-5 text-primary-600" />
                  <span className="text-sm font-medium text-gray-600">
                    {stat.name}
                  </span>
                </div>

                {/* Trend indicator */}
                {stat.trend && (
                  <div className={`flex items-center text-sm font-medium ${
                    stat.trend.direction === 'up' 
                      ? 'text-green-600' 
                      : stat.trend.direction === 'down'
                      ? 'text-red-600'
                      : 'text-gray-600'
                  }`}>
                    {stat.trend.direction === 'up' ? (
                      <TrendingUp className="w-4 h-4 mr-1" />
                    ) : stat.trend.direction === 'down' ? (
                      <TrendingDown className="w-4 h-4 mr-1" />
                    ) : null}
                    {stat.trend.type === 'percentage' 
                      ? `${stat.trend.value.toFixed(1)}%` 
                      : stat.trend.value.toFixed(0)
                    } {stat.trend.comparison_period}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Job Submissions Tracking */}
      <JobSubmissionsTracking jobSubmissions={data.job_submissions} />

      {/* Match Status Breakdown and Source Analytics - Side by side on desktop */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Match Status Breakdown */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Match Status Breakdown</h3>
          </div>
          <div className="card-body">
            {data.status_breakdown.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Target className="w-12 h-12 text-gray-400 mb-3" aria-hidden="true" />
                <p className="text-gray-600 text-sm">No matches have been applied yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {/* Total above chart */}
                <div className="text-center">
                  <div className="text-3xl font-bold text-gray-900">
                    {data.status_breakdown.reduce((sum, item) => sum + item.count, 0)}
                  </div>
                  <div className="text-sm text-gray-500">Total Matches</div>
                </div>

                {/* Pie chart with percentage labels */}
                <div className="flex items-center justify-center">
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={data.status_breakdown.map(item => ({
                          name: item.status,
                          value: item.count,
                          percentage: item.percentage,
                          color: item.color
                        }))}
                        cx="50%"
                        cy="50%"
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="value"
                        label={(props: any) => {
                          const { cx, cy, midAngle, innerRadius, outerRadius, percentage } = props;
                          // Only show label if segment is large enough (>= 5%)
                          if (percentage < 5) return null;
                          
                          const RADIAN = Math.PI / 180;
                          const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
                          const x = cx + radius * Math.cos(-midAngle * RADIAN);
                          const y = cy + radius * Math.sin(-midAngle * RADIAN);
                          
                          return (
                            <text 
                              x={x} 
                              y={y} 
                              fill="white" 
                              textAnchor="middle" 
                              dominantBaseline="central"
                              className="text-sm font-semibold"
                            >
                              {`${percentage}%`}
                            </text>
                          );
                        }}
                        labelLine={false}
                      >
                        {data.status_breakdown.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Legend */}
                <div className="space-y-2">
                  {data.status_breakdown.map((item) => (
                    <div key={item.status} className="flex items-center justify-between py-1.5">
                      <div className="flex items-center space-x-2">
                        <div 
                          className="w-3 h-3 rounded-full flex-shrink-0" 
                          style={{ backgroundColor: item.color }}
                        ></div>
                        <span className="text-sm font-medium text-gray-700">
                          {item.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className="text-sm font-semibold text-gray-900">
                          {item.count}
                        </span>
                        <span className="text-sm text-gray-500">
                          ({item.percentage}%)
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Source Analytics */}
        <SourceAnalyticsCard
          resumeSources={data.resume_sources || []}
          jobSources={data.job_sources || []}
          isLoading={loading}
        />
      </div>
    </div>
  )
}

export default DashboardPage
